<template lang="pug">
nuxt-img(:src="imageSrc")
</template>

<script setup lang="ts">
import type { Images, ImageSize } from '~/types'

const props = defineProps({
	images: {
		type: Object as PropType<Images | undefined | null>,
		required: true
	},
	size: {
		type: String as PropType<ImageSize>,
		required: true
	},
	preferSmallerFallback: {
		type: Boolean,
		default: false
	},
	fallbackType: {
		type: String as PropType<'song' | 'artist' | 'playlist' | 'venue'>,
		default: 'song',
		required: true
	}
})

/**
 * Get the preferred size of an image from the images object, or the next largest available - or smaller if `preferSmallerFallback` is true.
 * Will return a placeholder if no image is available.
 *
 * @returns {string} - Image URL from the image object, or a placeholder if not defined
 */
const getImage = (
	images: Images | undefined | null,
	size: ImageSize,
	preferSmallerFallback: boolean,
	fallbackType: 'song' | 'artist' | 'playlist' | 'venue'
) => {
	if (!images) {
		const extension = fallbackType === 'venue' ? 'jpg' : 'svg'
		return `/${fallbackType}-placeholder.${extension}`
	}
	const sizes = {
		tiny: 1,
		small: 2,
		medium: 3,
		large: 4,
		xlarge: 5
	}
	const placeholders = [
		'song',
		'artist',
		'venue',
		'playlist'
	]
	const imageArray = Object.entries(sizes).map(([key, sizeValue]) => {
		const isDefined = images[key as keyof typeof sizes]
		return {
			data: isDefined,
			size: key as keyof typeof sizes,
			sizeValue
		}
	}).sort((a, b) => a.sizeValue - b.sizeValue)
	const exactSize = imageArray.find(n => n.size === size && n.data && n.data.url)
	const sizeIndex = sizes[size as keyof typeof sizes] ? sizes[size as keyof typeof sizes] : 3
	const getLarger = (): string | undefined => {
		for (let i = sizeIndex; i <= 5; i++) {
			const img = imageArray[i - 1]
			if (img.data && img.data.url) {
				return img.data.url
			}
		}
	}
	const getSmaller = (): string | undefined => {
		for (let i = sizeIndex; i > 0; i--) {
			const img = imageArray[i - 1]
			if (img.data && img.data.url) {
				return img.data.url
			}
		}
	}
	const preferSmaller = (): string | undefined => {
		return getSmaller() || getLarger()
	}
	const preferLarger = (): string | undefined => {
		return getLarger() || getSmaller()
	}
	if (!sizes[size as keyof typeof sizes]) {
		console.error(`Invalid image size: "${size}", valid image sizes: ${Object.keys(sizes).join(', ')}`)
		return preferLarger() || 'fallback-url'
	}
	if (!placeholders.includes(fallbackType)) {
		console.error(`Invalid placeholder: "${fallbackType}", valid placeholder fallbacks: ${placeholders.join(', ')}`)
		return preferLarger() || 'fallback-url'
	} else if (exactSize && exactSize.data && exactSize.data.url) {
		return exactSize.data.url
	} else {
		return preferSmallerFallback ? preferSmaller() : preferLarger()
	}
}

const imageSrc = computed(() => getImage(props.images, props.size, props.preferSmallerFallback, props.fallbackType))
</script>
